import React, { useState } from 'react';
import { SelectContent, SelectItem, SelectSeparator } from '../ui/select';
import { Input } from '../ui/input';
import { useCurrencies } from '../../hooks/useCurrencies';

/**
 * Drop-in <SelectContent> for currency pickers: every ISO-4217 currency,
 * common ones first, with a search box. Use inside a shadcn <Select>.
 *   <Select value={ccy} onValueChange={setCcy}>
 *     <SelectTrigger>…</SelectTrigger>
 *     <CurrencySelectContent pin={business?.currency} />
 *   </Select>
 */
export const CurrencySelectContent = ({ pin, compact = false, testId = 'currency-select-content' }) => {
  const { currencies, commonCount } = useCurrencies(pin);
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? currencies.filter((c) => c.code.toLowerCase().includes(needle) || (c.name || '').toLowerCase().includes(needle))
    : currencies;

  return (
    <SelectContent className="max-h-80" data-testid={testId}>
      <div className="px-2 pt-1 pb-2 sticky top-0 bg-popover z-10">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
          placeholder="Search currency…"
          className="h-8 text-xs"
          data-testid={`${testId}-search`}
        />
      </div>
      {filtered.map((c, i) => (
        <React.Fragment key={c.code}>
          {!needle && i === commonCount && <SelectSeparator />}
          <SelectItem value={c.code} data-testid={`currency-option-${c.code}`}>
            {compact ? (
              <span>{c.symbol && c.symbol !== c.code ? `${c.symbol} ` : ''}{c.code}</span>
            ) : (
              <span className="flex items-center gap-2">
                {c.flag && <span>{c.flag}</span>}
                <span className="font-medium">{c.code}</span>
                <span className="text-muted-foreground text-xs truncate">{c.name}</span>
              </span>
            )}
          </SelectItem>
        </React.Fragment>
      ))}
      {!filtered.length && <div className="px-3 py-2 text-xs text-muted-foreground">No currency matches “{q}”</div>}
    </SelectContent>
  );
};

/** Plain <option> list for native <select> elements. */
export const CurrencyOptions = ({ pin }) => {
  const { currencies } = useCurrencies(pin);
  return currencies.map((c) => (
    <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
  ));
};

export default CurrencySelectContent;
